import React from "react";
import { Box, Button, styled, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { ItemCardType } from "../../types/types";
import LazyImage from "../LazyLoadingImage/LazyImage";
import { calculateDiscountedPrice, formatCOP } from "../../utils/format";
import { DiscountBadge, OriginalPrice } from "../../pages/shared/styledComponents";
import ShareButton from "../ShareButton/ShareButton";
import StarRating from "../StarRating/StarRating";
import FavoriteButton from "../FavoriteButton/FavoriteButton";
import AvailabilityChip from "../AvailabilityChip/AvailabilityChip";
import { AVAILABILITY_TEXTS } from "../../pages/shared/constants";
import { resolveAssetUrl, resolveThumbUrl } from "../../utils/assets";
import { gaEvent, toGaItem } from "../../analytics";
import { CardContainer, CardContent, CARD_IMAGE_HEIGHT, ImageWrapper } from "./styledComponents";

const QuotePrice = styled(Typography)(({ theme }) => ({
  fontFamily: "Antonio, sans-serif",
  fontWeight: 700,
  fontSize: 14,
  color: theme.palette.purchase.quote,
}));

const SeeMoreButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.common.white,
  textTransform: "none",
  borderRadius: "20px 20px 0 20px",
  boxShadow: theme.shadows[3],
  fontSize: 9,
  fontWeight: 600,
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  "&:hover": {
    backgroundColor: theme.palette.text.secondary,
  },
}));

interface PublicItemProps {
  item: ItemCardType;
  /** GA4 `item_list_name` — which grid this card was clicked from (EB-558). */
  listName?: string;
  /** Position within that list, so "seen but never clicked" can be read off by rank. */
  index?: number;
}

export const PublicItem: React.FC<PublicItemProps> = ({ item, listName, index }) => {
  /**
   * The 320px WebP thumbnail when there can be one, the original otherwise (EB-518).
   *
   * Measured on prod: the originals are all 600x600 and average 151 KB for a card that renders at
   * 160px, which put ~7.2 MB on a first paint of 48 cards. The thumbnails are ~35x smaller.
   *
   * `useState` rather than a plain expression because the fallback has to survive a failed load:
   * before the backfill runs, most products have no thumbnail, and `onError` is the only signal
   * that a given one is missing.
   */
  const original = resolveAssetUrl(item.picture);
  const thumb = resolveThumbUrl(item.picture);
  const [src, setSrc] = React.useState(thumb || original);

  // A different product in the same card slot must not inherit the previous one's fallback.
  React.useEffect(() => setSrc(thumb || original), [thumb, original]);

  /**
   * `select_item` closes the pair `view_item_list` opens (EB-558): with both, GA can rank a list by
   * click-through instead of only reporting that a product page was reached somehow.
   *
   * Fired from an `onClick` on the `<Link>` rather than from the product page, because only the
   * card knows which list and which position the click came from — by the time the route changes,
   * that context is gone.
   */
  const reportSelect = () =>
    gaEvent("select_item", {
      ...(listName ? { item_list_name: listName } : {}),
      items: [toGaItem(item, index != null ? { index } : {})],
    });

  return (
    <CardContainer>
      <Box display="flex" justifyContent="flex-end" alignItems="center" px={1} pt={0.5}>
        {item.rating != null && (
          <Box mr="auto">
            <StarRating value={item.rating} />
          </Box>
        )}
        <ShareButton name={item.name} productId={item.id} />
        {item.id && <FavoriteButton itemId={item.id} />}
      </Box>
      <Link to={"/producto/" + item.id} style={{ textDecoration: "none" }} onClick={reportSelect}>
        <ImageWrapper sx={{ cursor: "pointer" }}>
          {item.picture && (
            <LazyImage
              src={src}
              alt={item.name}
              // One retry only: if the original fails too, there is nothing further to try and a
              // loop would hammer the CDN on every broken product.
              onError={() => src !== original && setSrc(original)}
              style={{ width: "100%", height: CARD_IMAGE_HEIGHT, objectFit: "contain" }}
            />
          )}
        </ImageWrapper>
      </Link>

      <CardContent>
        {!!item.price && item.discount > 0 ? (
          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
            <OriginalPrice
              sx={{ fontFamily: "Antonio, sans-serif", fontWeight: 700, fontSize: 16 }}
            >
              {formatCOP(item.price)}
            </OriginalPrice>
            <Typography
              color="success.main"
              sx={{ fontFamily: "Antonio, sans-serif", fontWeight: 700, fontSize: 16 }}
            >
              {formatCOP(calculateDiscountedPrice(item.price, item.discount))}
            </Typography>
            <DiscountBadge label={`-${item.discount}%`} size="small" />
          </Box>
        ) : item.price ? (
          <Typography sx={{ fontFamily: "Antonio, sans-serif", fontWeight: 700, fontSize: 16 }}>
            {formatCOP(item.price)}
          </Typography>
        ) : (
          // 107 of the 307 live products have no price, and this branch used to render nothing at
          // all — a blank where the price goes, which reads as a broken card rather than as "we
          // have to quote this one" (docs/ordering-flow/README.md §3.3).
          <QuotePrice>{AVAILABILITY_TEXTS.quotePrice}</QuotePrice>
        )}

        <Typography color="text.primary" sx={{ fontSize: 12 }}>
          {item.name}
        </Typography>

        <Box mt={0.5}>
          <AvailabilityChip item={item} />
        </Box>

        <Box mt="auto" pt={1} display="flex" justifyContent="flex-end">
          <Link
            to={"/producto/" + item.id}
            style={{ textDecoration: "none" }}
            onClick={reportSelect}
          >
            <SeeMoreButton size="small">Ver más</SeeMoreButton>
          </Link>
        </Box>
      </CardContent>
    </CardContainer>
  );
};
